import { Activity } from 'lucide-react';
import { type JSX, type ReactNode,useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import {
  selectAuthStatus,
  sessionEstablished,
  sessionFailed,
  sessionStarted,
  useCurrentUser,
} from '@/features/auth';

interface SessionLoaderProps {
  readonly children: ReactNode;
}

/**
 * Al montar la app, intenta hidratar la sesión llamando GET /auth/me.
 * El backend valida la cookie HttpOnly (`auth_token`) automáticamente vía
 * `withCredentials: true`. Si la cookie es válida → sesión rehidratada.
 * Si no → status quedará en `unauthenticated` y los guards redirigen.
 *
 * Mientras esto resuelve mostramos un splash discreto, no la app entera.
 */
export function SessionLoader({ children }: SessionLoaderProps): JSX.Element {
  const dispatch = useAppDispatch();
  const status = useAppSelector(selectAuthStatus);
  const meQuery = useCurrentUser();

  useEffect(() => {
    if (status === 'idle' && meQuery.isPending) {
      dispatch(sessionStarted());
    }
  }, [status, meQuery.isPending, dispatch]);

  useEffect(() => {
    if (meQuery.isSuccess) {
      dispatch(sessionEstablished({ user: meQuery.data, accessToken: null }));
    } else if (meQuery.isError) {
      dispatch(sessionFailed());
    }
  }, [meQuery.isSuccess, meQuery.isError, meQuery.data, dispatch]);

  if (status === 'idle' || status === 'authenticating') {
    return <SessionSplash />;
  }

  return <>{children}</>;
}

function SessionSplash(): JSX.Element {
  return (
    <div className="grid min-h-screen place-items-center bg-background">
      <div className="absolute inset-0 bg-gradient-mesh opacity-50" aria-hidden />
      <div className="relative flex flex-col items-center gap-4 text-center">
        <span className="grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-primary to-accent shadow-soft-3">
          <Activity className="h-6 w-6 animate-pulse-soft text-primary-foreground" />
        </span>
        <div>
          <div className="font-display text-lg font-semibold tracking-tight text-foreground">
            SISMA
          </div>
          <div className="text-xs text-muted-foreground">Iniciando sesión…</div>
        </div>
      </div>
    </div>
  );
}
